import { provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { APP_INITIALIZER, ErrorHandler, Injectable, NgModule, isDevMode } from "@angular/core";
import { BrowserModule, HammerGestureConfig, HammerModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import {
    ActivatedRouteSnapshot,
    BaseRouteReuseStrategy,
    DefaultUrlSerializer,
    Router,
    RouteReuseStrategy,
    UrlSegmentGroup,
    UrlSerializer,
    UrlTree,
} from "@angular/router";
import { ServiceWorkerModule } from "@angular/service-worker";
import { FaConfig, FaIconLibrary, FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { far } from "@fortawesome/free-regular-svg-icons";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { EditorModule } from "@progress/kendo-angular-editor";
import { ToastrModule } from "ngx-toastr";
import { AppRootComponent } from "./app-root.component";
import { AppRoutingModule } from "./app-routing.module";
import { ArcusCommonModule } from "./Common/common.module";
import { AuthModule } from "@auth0/auth0-angular";
import { ArcusMaterialModule } from "./Material/material.module";
import { ReleaseDialogComponent } from "./release-dialog/release-dialog.component";
import { FlagDevOverlayComponent } from "./Common/flag-dev-overlay/flag-dev-overlay.component";
import { ThemeDevToggleComponent } from "./Common/theme-dev-toggle/theme-dev-toggle.component";
import { authCodeFlowConfig } from "./core/auth-config";
import { DIRECTION_ALL } from "hammerjs";
import * as Sentry from "@sentry/angular";

@Injectable()
export class MyHammerConfig extends HammerGestureConfig {
    overrides = <any>{
        swipe: { direction: DIRECTION_ALL },
    };
}
@Injectable()
export class LowerCaseUrlSerializer extends DefaultUrlSerializer {
    parse(url: string): UrlTree {
        const urlTree = super.parse(url);
        this.lowerCaseSegments(urlTree.root);
        return urlTree;
    }

    private lowerCaseSegments(urlSegmentGroup: UrlSegmentGroup) {
        if (urlSegmentGroup.hasChildren()) {
            Object.entries(urlSegmentGroup.children).forEach(([_key, value]) => this.lowerCaseSegments(value));
        }

        urlSegmentGroup.segments.forEach((segment) => (segment.path = segment.path.toLowerCase()));
    }
}
export class AppRouteReuseStrategy extends BaseRouteReuseStrategy {
    public shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        if (future.data.hasOwnProperty("reuseComponent")) return future.data.reuseComponent;
        return false;
    }
}
@NgModule({
    declarations: [AppRootComponent, ReleaseDialogComponent],
    bootstrap: [AppRootComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AuthModule.forRoot(authCodeFlowConfig),
        AppRoutingModule,
        ArcusCommonModule,
        ArcusMaterialModule,
        EditorModule,
        FontAwesomeModule,
        HammerModule,
        FlagDevOverlayComponent,
        ThemeDevToggleComponent,
        ToastrModule.forRoot(),
        ServiceWorkerModule.register("ngsw-worker.js", {
            enabled: !isDevMode(),
            // Register the ServiceWorker as soon as the application is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: "registerWhenStable:30000",
        }),
    ],
    providers: [
        { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
        {
            provide: UrlSerializer,
            useClass: LowerCaseUrlSerializer,
        },
        provideHttpClient(withInterceptorsFromDi()),
        // provideClientHydration(withEventReplay()),
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        },
        { provide: Sentry.TraceService, deps: [Router] },
        { provide: APP_INITIALIZER, useFactory: () => () => {}, deps: [Sentry.TraceService], multi: true },
    ],
})
export class AppModule {
    constructor(library: FaIconLibrary, faConfig: FaConfig) {
        library.addIconPacks(far, fas);
        faConfig.fixedWidth = true;
    }
}
